import COMMON_ROUTES from '@/presentation/constants/routes/common.constant.js';
import { authRoutes } from '@/presentation/modules/auth';
import { categoryRoutes } from '@/presentation/modules/category';
import { dashboardRoutes } from '@/presentation/modules/dashboard';
import { settingRoutes } from '@/presentation/modules/setting';
import { requestRoutes } from '@/presentation/modules/request';
import { profileRoutes } from '@/presentation/modules/profile';
import { professionalRoutes } from '@/presentation/modules/professional';

const commonRoutes = Object.freeze([
	{
		name: COMMON_ROUTES.root.name,
		path: COMMON_ROUTES.root.path,
		redirect: COMMON_ROUTES.root.redirect,
		children: [
			{
				name: COMMON_ROUTES.search.name,
				path: COMMON_ROUTES.search.path,
				meta: COMMON_ROUTES.search.meta,
				component: COMMON_ROUTES.search.component,
			},
			{
				name: COMMON_ROUTES.home.name,
				path: COMMON_ROUTES.home.path,
				meta: COMMON_ROUTES.home.meta,
				component: COMMON_ROUTES.home.component,
			},
			{
				name: COMMON_ROUTES.explore.name,
				path: COMMON_ROUTES.explore.path,
				meta: COMMON_ROUTES.explore.meta,
				component: COMMON_ROUTES.explore.component,
			},
			{
				name: COMMON_ROUTES.payment.name,
				path: COMMON_ROUTES.payment.path,
				children: [...COMMON_ROUTES.payment.children],
			},
			...authRoutes,
			...categoryRoutes,
			...dashboardRoutes,
			...settingRoutes,
			...requestRoutes,
			...profileRoutes,
			...professionalRoutes,
		],
	},
	{
		name: 'Forbidden',
		path: '/403',
		meta: { layout: 'Simple', noindex: true },
		component: () => import('@/presentation/views/ForbiddenView.vue'),
	},
	{
		name: COMMON_ROUTES.requestExpired.name,
		path: COMMON_ROUTES.requestExpired.path,
		meta: COMMON_ROUTES.requestExpired.meta,
		props: COMMON_ROUTES.requestExpired.props,
		component: COMMON_ROUTES.requestExpired.component,
	},
	{
		name: COMMON_ROUTES.installApp.name,
		path: COMMON_ROUTES.installApp.path,
		meta: COMMON_ROUTES.installApp.meta,
		component: COMMON_ROUTES.installApp.component,
	},
	{
		name: COMMON_ROUTES.iosPwaInstallGuide.name,
		path: COMMON_ROUTES.iosPwaInstallGuide.path,
		meta: COMMON_ROUTES.iosPwaInstallGuide.meta,
		component: COMMON_ROUTES.iosPwaInstallGuide.component,
	},
	{
		name: COMMON_ROUTES.contactUs.name,
		path: COMMON_ROUTES.contactUs.path,
		meta: COMMON_ROUTES.contactUs.meta,
		component: COMMON_ROUTES.contactUs.component,
	},
	{
		name: COMMON_ROUTES.termsAndCondition.name,
		path: COMMON_ROUTES.termsAndCondition.path,
		meta: COMMON_ROUTES.termsAndCondition.meta,
		component: COMMON_ROUTES.termsAndCondition.component,
	},
	{
		name: COMMON_ROUTES.depositCancellationPolicy.name,
		path: COMMON_ROUTES.depositCancellationPolicy.path,
		meta: COMMON_ROUTES.depositCancellationPolicy.meta,
		component: COMMON_ROUTES.depositCancellationPolicy.component,
	},
	{
		name: 'AdminBusinessesList',
		path: '/admin/businesses',
		alias: ['/admin/business', '/admin/bussiness'],
		meta: {
			layout: 'Blank',
			requiresAuth: true,
			allowedRoles: ['Admin'],
		},
		component: () => import('@/presentation/modules/dashboard/views/admin/AdminBusinessesListPage.vue'),
	},
	{
		name: 'AdminBusinessCreate',
		path: '/admin/businesses/create',
		meta: {
			layout: 'Simple',
			requiresAuth: true,
			allowedRoles: ['Admin'],
		},
		component: () => import('@/presentation/modules/dashboard/views/admin/CreateBusiness.vue'),
	},
	{
		name: 'AdminBusinessEdit',
		path: '/admin/businesses/:businessId/edit',
		meta: {
			layout: 'Simple',
			requiresAuth: true,
			allowedRoles: ['Admin'],
		},
		component: () => import('@/presentation/modules/dashboard/views/admin/EditBusiness.vue'),
	},
	{
		name: 'PublicProfessionalProfile',
		path: '/professionals/:slug',
		meta: {
			layout: 'Simple',
		},
		component: () => import('@/presentation/modules/professional/views/PublicProfessionalProfileView.vue'),
	},
	{
		name: 'PublicProfileSlug',
		path: '/:citySlug/profile/:businessSlug',
		meta: {
			layout: 'Simple',
		},
		component: () => import('@/presentation/modules/profile/views/shop-view/ShopView.vue'),
	},
	{
		name: 'PublicProfileSlugPost',
		path: '/:citySlug/profile/:businessSlug/post/:postId',
		meta: {
			layout: 'Simple',
		},
		component: () => import('@/presentation/modules/profile/views/PostView.vue'),
	},
	{
		name: 'ReservationServiceSlug',
		path: '/:citySlug/profile/:businessSlug/reserve',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
		},
		component: () => import('@/presentation/modules/profile/views/ServiceView.vue'),
	},
	{
		name: 'ReservationArtistSlug',
		path: '/:citySlug/profile/:businessSlug/reserve/artist',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
		},
		component: () => import('@/presentation/modules/profile/views/ArtistView.vue'),
	},
	{
		name: 'ReservationTimeSlug',
		path: '/:citySlug/profile/:businessSlug/reserve/time',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
		},
		component: () => import('@/presentation/modules/profile/views/ChooseTimeView.vue'),
	},
	{
		name: 'ReservationSummarySlug',
		path: '/:citySlug/profile/:businessSlug/reserve/summary',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
			noindex: true,
		},
		component: () => import('@/presentation/modules/profile/views/ReservationGuestView.vue'),
	},
	{
		name: 'ReservationPaymentSlug',
		path: '/:citySlug/profile/:businessSlug/reserve/payment',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
			requiresAuth: true,
			noindex: true,
		},
		component: () => import('@/presentation/views/payment/PreviewPaymentView.vue'),
	},
	{
		name: 'SubscriptionPaymentResult',
		path: '/subscriptions/payment/result',
		meta: {
			layout: 'Simple',
			requiresAuth: true,
			noindex: true,
		},
		component: () => import('@/presentation/views/payment/SubscriptionPaymentResultView.vue'),
	},
	{
		name: 'LegacyReserveRoot',
		path: '/reserve/:shopId',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
		},
		component: () => import('@/presentation/modules/profile/views/ServiceView.vue'),
	},
	{
		name: 'LegacyProfileReserveRoot',
		path: '/profile/:shopId/reserve',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
		},
		component: () => import('@/presentation/modules/profile/views/ServiceView.vue'),
	},
	{
		name: 'LegacyBusinessReserveRoot',
		path: '/business/:shopId/reserve',
		meta: {
			layout: 'Simple',
			blockedRoles: ['Business'],
		},
		component: () => import('@/presentation/modules/profile/views/ServiceView.vue'),
	},
	{
		name: COMMON_ROUTES.notFound.name,
		path: COMMON_ROUTES.notFound.path,
		meta: COMMON_ROUTES.notFound.meta,
		component: COMMON_ROUTES.notFound.component,
	},
]);

export default commonRoutes;
